import { FC, useMemo } from 'react';

import { motion } from 'framer-motion';

import { useAnalyticsReportingService } from '@ui-furniture/common/analytics';
import { x } from '@ui-furniture/style';

import { useFewerOptionsWhileCollapsed } from '../hooks/use-fewer-options-while-collapsed';
import { ButtonGrid } from '../ui/button-grid';
import { ExpandButton } from '../ui/expand-button';
import { ExpandableButtonGrid } from '../ui/expandable-button-grid';

export type SingleSelectChatInputConfig<TValue> = {
  options: Array<{
    label: string;
    value: TValue;
  }>;
};
export const SingleSelectChatInput: FC<{
  onAnswer: (option: { label: string; value: any }) => void;
  config: SingleSelectChatInputConfig<any>;
}> = ({ onAnswer, config: { options } }) => {
  const {
    canExpand,
    isCollapsed,
    toggleCollapse,
    collapsedOptions,
    expandedOptions,
  } = useFewerOptionsWhileCollapsed({
    options,
    collapsedCount: 4,
  });

  const { reportEvent } = useAnalyticsReportingService();

  const collapsedOptionsWithOnClick = useMemo(
    () =>
      collapsedOptions.map((option) => ({
        label: option.label,
        onClick: () => onAnswer(option),
      })),
    [collapsedOptions, onAnswer]
  );
  const expandedOptionsWithOnClick = useMemo(
    () =>
      expandedOptions.map((option) => ({
        label: option.label,
        onClick: () => onAnswer(option),
      })),
    [expandedOptions, onAnswer]
  );

  return (
    <motion.div
      layout
      style={{ overflow: isCollapsed ? 'hidden' : 'auto', padding: '1px' }}
    >
      {canExpand ? (
        <ExpandButton
          expandDirection='up'
          toggleCollapse={() => {
            toggleCollapse();
            reportEvent({
              name: 'Click',
              on: 'Chatbot - Expand button',
            });
          }}
          isCollapsed={isCollapsed}
          text={
            isCollapsed
              ? `Show More (+${expandedOptionsWithOnClick.length})`
              : 'Show Less'
          }
          pb='sm !important'
        />
      ) : null}
      <x.div mb='sm'>
        <ButtonGrid options={collapsedOptionsWithOnClick} />
      </x.div>
      <ExpandableButtonGrid
        options={expandedOptionsWithOnClick}
        isCollapsed={isCollapsed}
      />
    </motion.div>
  );
};
